import clsx from 'clsx'
import React, { useState } from 'react'
import { debounce } from 'lodash'
import { makeStyles, useTheme } from '@mui/styles'
import { CircularProgress, Tooltip } from '@mui/material'

import ThemeInterface from '@/theme-interface'
import EmailIcon from '@assets/images/icons/email.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import lang from '@/lang'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    borderBottom: `1px solid ${theme.borderColors.default}`,
    alignItems: 'center',
    padding: '5px 0px',
    '&:first-of-type': {
      paddingTop: 0,
    },
    '&:last-of-type': {
      borderBottom: 'none',
      paddingBottom: 0,
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 0 80%',
  },
  emailWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  name: {
    fontSize: theme.fontSizes.default,
  },
  email: {
    fontSize: theme.fontSizes.smaller,
    color: theme.textLight,
    lineHeight: '21px',
  },
  emailIcon: {
    marginLeft: 5,
    cursor: 'pointer',
    '&:hover': {
      color: theme.primary,
      fill: theme.primary,
    },
  },
  active: {
    borderBottom: 'none',
    paddingBottom: 5,
    '& $name': {
      fontSize: theme.fontSizes.large,
    },
    '& $email': {
      fontSize: theme.fontSizes.smaller,
      color: theme.textLight,
    },
  },
  tooltip: {
    zIndex: 1502,
  },
  loader: {
    marginLeft: 5,
  }
}))

type SignatureSigningInfoSignerItemProps = {
  name: string,
  email: string,
  active?: boolean,
  onSend: (email: string) => void
}

const SignatureSigningInfoSignerItem = (props: SignatureSigningInfoSignerItemProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const { name, email, active = false, onSend } = props
  const [isLoading, setIsLoading] = useState(false)

  const debouncedSend = debounce(() => {
    onSend && onSend(email)
    setIsLoading(false)
  }, 1500)

  const sendIcon = isLoading
    ? <CircularProgress className={classes.loader} size={10} color='primary' />
    : (
      <Tooltip
        title={lang.signature.signing.info.signers.resendTooltip}
        classes={{ popper: classes.tooltip }}>
        <SVGIcon
          svg={EmailIcon}
          height='10px'
          width='10px'
          className={classes.emailIcon}
          fill={theme.iconFillColorLight}
          onClick={() => {
            setIsLoading(true)
            debouncedSend()
          }} />
      </Tooltip>
    )

  const rootClass = clsx(classes.root, active && classes.active)
  return (
    <div className={rootClass}>
      <div className={classes.details}>
        <div className={classes.name}>{name}</div>
        <div className={classes.emailWrapper}>
          <div className={classes.email}>{email}</div>
          {onSend && sendIcon}
        </div>
      </div>
    </div>
  )
}

export default SignatureSigningInfoSignerItem
